import { MOCK_COMMUNITY_DATA, TRADER_PROFILES } from "./communityMockData";
import { withMockMarketPrices } from "./mockMarketPrices";

/**
 * Time range keys accepted by community endpoints.
 * Pass the same values to the real API as a query param later, e.g. ?range=1d
 */
export const COMMUNITY_TIME_RANGES = {
  ONE_DAY: "1d",
  ONE_WEEK: "1w",
  ONE_MONTH: "1m",
  ALL: "all",
};

export const COMMUNITY_TIME_RANGE_OPTIONS = [
  { id: COMMUNITY_TIME_RANGES.ONE_DAY, labelKey: "futures.community.range.1d" },
  { id: COMMUNITY_TIME_RANGES.ONE_WEEK, labelKey: "futures.community.range.1w" },
  {
    id: COMMUNITY_TIME_RANGES.ONE_MONTH,
    labelKey: "futures.community.range.1m",
  },
  {
    id: COMMUNITY_TIME_RANGES.ALL,
    labelKey: "futures.community.range.all",
  },
];

const RANGE_MS = {
  [COMMUNITY_TIME_RANGES.ONE_DAY]: 24 * 60 * 60 * 1000,
  [COMMUNITY_TIME_RANGES.ONE_WEEK]: 7 * 24 * 60 * 60 * 1000,
  [COMMUNITY_TIME_RANGES.ONE_MONTH]: 30 * 24 * 60 * 60 * 1000,
};

const BADGE_BY_RANK = {
  1: "badge-gold",
  2: "badge-silver",
  3: "badge-bronze",
};

const parseMoney = (value) => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(num) ? 0 : num;
};

/**
 * Client-side filter used only while mocks are active.
 * Real API should apply the same window server-side via `range`.
 */
export const filterByUpdatedAtRange = (items, range = COMMUNITY_TIME_RANGES.ALL) => {
  if (!range || range === COMMUNITY_TIME_RANGES.ALL) {
    return items;
  }

  const windowMs = RANGE_MS[range];
  if (!windowMs) {
    return items;
  }

  const cutoff = Date.now() - windowMs;

  return items.filter((item) => {
    const ts = new Date(item.updatedAt).getTime();
    return !Number.isNaN(ts) && ts >= cutoff;
  });
};

const sortByMoneyFieldDesc = (items, field) =>
  [...items].sort((a, b) => parseMoney(b[field]) - parseMoney(a[field]));

/** Rank 1–3 get gold / silver / bronze; others have no badge */
const withDisplayRanks = (items) =>
  items.map((item, index) => {
    const rank = index + 1;
    return {
      ...item,
      rank,
      badgeIcon: BADGE_BY_RANK[rank] ?? null,
    };
  });

const delay = (ms = 80) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Community data API facade.
 *
 * Swap mock bodies for real requests without changing callers:
 *   const { data } = await axios.get("/futures/community/leaderboard", { params: { range } });
 *   return data;
 */
export const communityService = {
  getPublicFeed: async () => {
    await delay();
    return withMockMarketPrices(MOCK_COMMUNITY_DATA.publicFeed);
    // return (await axios.get("/futures/community/public-feed")).data;
  },

  /**
   * @param {{ range?: string }} [params]
   */
  getLeaderboard: async ({ range = COMMUNITY_TIME_RANGES.ALL } = {}) => {
    await delay();
    const filtered = filterByUpdatedAtRange(
      MOCK_COMMUNITY_DATA.leaderboard,
      range,
    );
    const sorted = sortByMoneyFieldDesc(filtered, "biggestWin");
    return withDisplayRanks(sorted);
    // return (await axios.get("/futures/community/leaderboard", { params: { range } })).data;
  },

  /**
   * @param {{ range?: string }} [params]
   */
  getBestTrades: async ({ range = COMMUNITY_TIME_RANGES.ALL } = {}) => {
    await delay();
    const filtered = await withMockMarketPrices(
      filterByUpdatedAtRange(MOCK_COMMUNITY_DATA.bestTrades, range),
    );
    const sorted = sortByMoneyFieldDesc(filtered, "profit");
    return withDisplayRanks(sorted);
    // return (await axios.get("/futures/community/best-trades", { params: { range } })).data;
  },

  /**
   * Leaderboard stats + profile + the trader's trades and achievements.
   * @param {string} username
   */
  getTraderProfile: async (username) => {
    await delay();
    const stats =
      MOCK_COMMUNITY_DATA.leaderboard.find((row) => row.user === username) ??
      {};

    const profile = TRADER_PROFILES[username] ?? {};

    return {
      achievements: [],
      ...stats,
      ...profile,
      openTrades: await withMockMarketPrices(profile.openTrades ?? []),
      user: username,
    };
    // return (await axios.get(`/futures/community/traders/${username}`)).data;
  },
};
